import Decimal from 'decimal.js';

/**
 * 金額・単価・数量・率の計算（02 2.11・9.6、要件書 5.4・9.2）。人間レビュー必須（02 11.1 No.4）。
 * JavaScript の number で計算しない（02 11.2 No.5）。必ずこのファイルの関数を通す。
 *
 * - 精度 38 桁・丸めは断りが無ければ小数 2 桁の四捨五入（ROUND_HALF_UP。02 D-105）
 * - API の入出力は Decimal 文字列（`"1234.50"`）。DB は Number（小数 2 桁に丸めて保存・整数部 13 桁まで）
 */
const MONEY = Decimal.clone({ precision: 38, rounding: Decimal.ROUND_HALF_UP });

export type DecimalValue = InstanceType<typeof MONEY>;
export type DecimalInput = string | number | DecimalValue;

const MONEY_PATTERN = /^-?\d{1,13}(\.\d{1,2})?$/;

/** Decimal にする。number は DB から読んだ値（15 桁以内）だけを受ける */
export function toDecimal(value: DecimalInput): DecimalValue {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      throw new RangeError('有限の数ではありません');
    }
    return new MONEY(String(value));
  }
  return new MONEY(value);
}

/** 金額の文字列として正しいか（整数 13 桁・小数 2 桁まで・負を許す） */
export function isDecimalString(value: string): boolean {
  return MONEY_PATTERN.test(value);
}

/** 小数 2 桁の文字列にする（API の応答用。DB の Number → `"123456.00"`） */
export function toMoneyString(value: DecimalInput): string {
  return toDecimal(value).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toFixed(2);
}

/** DB に保存する Number にする（小数 2 桁に丸める） */
export function toDynamoNumber(value: DecimalInput): number {
  return toDecimal(value).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber();
}

export function addMoney(...values: DecimalInput[]): DecimalValue {
  return values.reduce<DecimalValue>((sum, value) => sum.plus(toDecimal(value)), new MONEY(0));
}

export function subtractMoney(value_a: DecimalInput, value_b: DecimalInput): DecimalValue {
  return toDecimal(value_a).minus(toDecimal(value_b));
}

export function multiplyMoney(value_a: DecimalInput, value_b: DecimalInput): DecimalValue {
  return toDecimal(value_a).times(toDecimal(value_b));
}

export function compareMoney(value_a: DecimalInput, value_b: DecimalInput): number {
  return toDecimal(value_a).comparedTo(toDecimal(value_b));
}

/** 粗利 = 販売単価 − 仕入単価（無ければ 0）− 営業支援費（無ければ 0）（02 9.6） */
export function calculateGrossProfit(
  sales_unit_price: DecimalInput,
  supplier_unit_price: DecimalInput | null | undefined,
  sales_support_fee: DecimalInput | null | undefined,
): string {
  return toMoneyString(
    toDecimal(sales_unit_price)
      .minus(toDecimal(supplier_unit_price ?? 0))
      .minus(toDecimal(sales_support_fee ?? 0)),
  );
}

/** 利益率 = 粗利 ÷ 販売単価 × 100（販売単価が 0 なら null）（02 9.6） */
export function calculateProfitRate(gross_profit: DecimalInput, sales_unit_price: DecimalInput): string | null {
  const price = toDecimal(sales_unit_price);
  if (price.isZero()) {
    return null;
  }
  return toMoneyString(toDecimal(gross_profit).dividedBy(price).times(100));
}

/** 加重金額 = amount × probability ÷ 100（02 9.6・D-038） */
export function calculateWeightedAmount(amount: DecimalInput, probability: DecimalInput): string {
  return toMoneyString(toDecimal(amount).times(toDecimal(probability)).dividedBy(100));
}

/** 成約率 = 成約数 ÷（成約数＋失注数）× 100（分母 0 なら 0）（02 9.6） */
export function calculateWinRate(won_count: DecimalInput, lost_count: DecimalInput): string {
  const denominator = toDecimal(won_count).plus(toDecimal(lost_count));
  if (denominator.isZero()) {
    return toMoneyString(0);
  }
  return toMoneyString(toDecimal(won_count).dividedBy(denominator).times(100));
}

/** 進捗率 = progress_value ÷ target_value × 100（target_value が 0 なら 0）（02 9.6） */
export function calculateProgressRate(progress_value: DecimalInput, target_value: DecimalInput): string {
  const target = toDecimal(target_value);
  if (target.isZero()) {
    return toMoneyString(0);
  }
  return toMoneyString(toDecimal(progress_value).dividedBy(target).times(100));
}

/**
 * 画面の金額表示（03 3.9.2・FE-D-042）：3 桁区切り、小数部が .00 なら省く。負は `-¥1,234`。
 */
export function formatMoney(value: DecimalInput): string {
  const fixed = toDecimal(value).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
  const is_negative = fixed.isNegative() && !fixed.isZero();
  const [integer_part = '0', fraction_part = '00'] = fixed.abs().toFixed(2).split('.');
  const grouped = integer_part.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const body = fraction_part === '00' ? grouped : `${grouped}.${fraction_part}`;
  return `${is_negative ? '-' : ''}¥${body}`;
}

/** 率の表示（03 3.9.2・FE-D-043）：小数 1 桁の % */
export function formatRate(value: DecimalInput | null): string {
  if (value === null) {
    return '—';
  }
  return `${toDecimal(value).toDecimalPlaces(1, Decimal.ROUND_HALF_UP).toFixed(1)}%`;
}

/** CSV の金額（02 5.6.1）：小数 2 桁の数字（桁区切りなし）。小数部が 00 なら整数 */
export function toCsvMoneyText(value: DecimalInput): string {
  const fixed = toDecimal(value).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toFixed(2);
  return fixed.endsWith('.00') ? fixed.slice(0, -3) : fixed;
}
